import React from 'react';
import { 
  GraduationCap, 
  User, 
  Calendar, 
  MapPin, 
  Clock, 
  Users,
  Award,
  FileText
} from 'lucide-react';

const SoutenanceDetails = ({ soutenance }) => {
  if (!soutenance) return null;

  const getStatutColor = (statut) => {
    const colors = {
      'PLANIFIEE': 'bg-blue-100 text-blue-800',
      'EN_COURS': 'bg-yellow-100 text-yellow-800',
      'TERMINEE': 'bg-green-100 text-green-800',
      'REPORTEE': 'bg-orange-100 text-orange-800',
      'ANNULEE': 'bg-red-100 text-red-800'
    };
    return colors[statut] || 'bg-gray-100 text-gray-800';
  };

  // Format proper date display
  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    try {
      // Correction pour plusieurs formats de date problématiques
      let correctedDateString = dateString;
      
      // Correction pour les années commençant par 0 (ex: 0205 -> 2025)
      if (dateString.match(/^0\d{3}-/)) {
        correctedDateString = `2${dateString.substring(1)}`;
      }
      
      // Correction pour les formats avec timezone bizarre (+00:13:35)
      correctedDateString = correctedDateString.replace(/\+00:\d{2}:\d{2}/, '+00:00');
      
      // Si la date est toujours invalide, essayons de l'extraire manuellement
      const date = new Date(correctedDateString);
      if (isNaN(date.getTime())) {
        // Extraction manuelle avec regex
        const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})T?/);
        if (match) {
          let [_, year, month, day] = match;
          // Si l'année commence par 0, corrigeons-la
          if (year.startsWith('0')) {
            year = `2${year.substring(1)}`;
          }
          return `${day}/${month}/${year}`;
        }
        return 'Date invalide';
      }
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      console.error("Erreur lors du formatage de la date:", e, dateString);
      return 'Date invalide';
    }
  };

  // Format proper time display
  const formatTime = (dateString) => {
    if (!dateString) return 'Non définie';
    try {
      // Correction pour plusieurs formats de date problématiques
      let correctedDateString = dateString;
      
      // Correction pour les années commençant par 0 (ex: 0205 -> 2025)
      if (dateString.match(/^0\d{3}-/)) {
        correctedDateString = `2${dateString.substring(1)}`;
      }
      
      // Correction pour les formats avec timezone bizarre (+00:13:35)
      correctedDateString = correctedDateString.replace(/\+00:\d{2}:\d{2}/, '+00:00');
      
      // Si la date est toujours invalide, essayons d'extraire l'heure manuellement
      const date = new Date(correctedDateString);
      if (isNaN(date.getTime())) {
        // Extraction manuelle avec regex
        const match = dateString.match(/T(\d{2}):(\d{2}):(\d{2})/);
        if (match) {
          const [_, hours, minutes] = match;
          return `${hours}:${minutes}`;
        }
        return 'Heure invalide';
      }
      
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error("Erreur lors du formatage de l'heure:", e, dateString);
      return 'Heure invalide';
    }
  };

  // Format duration to display properly
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return 'Non définie';
    const heures = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return heures > 0 
      ? `${heures}h${mins > 0 ? mins.toString().padStart(2, '0') : ''}`
      : `${mins} minutes`;
  };

  // Method to get all students information
  const getEtudiants = () => {
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      return soutenance.etudiants_soutenance
        .filter(es => es && es.etudiant)
        .sort((a, b) => (a.ordre_presentation || 0) - (b.ordre_presentation || 0));
    }
    // Legacy support
    return soutenance.etudiant ? [{ etudiant: soutenance.etudiant, role: 'PRINCIPAL' }] : [];
  };

  // Gets primary student or first student if available
  const getPrimaryStudent = () => {
    const etudiants = getEtudiants();
    
    // First try to find a student with PRINCIPAL role
    const principal = etudiants.find(e => e.role === 'PRINCIPAL');
    if (principal && principal.etudiant) {
      return principal.etudiant;
    }
    
    // Otherwise return the first student
    return etudiants.length > 0 ? etudiants[0].etudiant : null;
  };

  // Determine if it's a collective defense
  const isCollective = soutenance.est_collective || 
                      (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 1) ||
                      (soutenance.nombre_etudiants && soutenance.nombre_etudiants > 1);

  // The primary student to display if not collective
  const primaryStudent = getPrimaryStudent();

  return (
    <div className="space-y-6">
      {/* Informations générales */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
          <GraduationCap className="h-5 w-5" />
          <span>Informations générales</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* If collective, show number of students, otherwise show the primary student */}
          <div>
            <span className="text-sm font-medium text-gray-700">
              {isCollective ? 'Étudiants:' : 'Étudiant:'}
            </span>
            {isCollective ? (
              <p className="text-gray-900">
                {getEtudiants().length} participants
              </p>
            ) : (
              <p className="text-gray-900">
                {primaryStudent ? 
                  `${primaryStudent.nom || ''} ${primaryStudent.prenom || ''}`.trim() : 
                  'Non défini'
                }
              </p>
            )}
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700">Type:</span>
            <p className="text-gray-900">{soutenance.type_soutenance?.nom || 'Non défini'}</p>
          </div>
          <div className="md:col-span-2">
            <span className="text-sm font-medium text-gray-700">Titre:</span>
            <p className="text-gray-900">{soutenance.titre_travail || 'Non défini'}</p>
          </div>
        </div>
        
        {/* Show student list if collective */}
        {isCollective && getEtudiants().length > 0 && (
          <div className="mt-3 border-t border-gray-100 pt-3">
            <span className="text-sm font-medium text-gray-700 block mb-2">Liste des étudiants:</span>
            <div className="space-y-1">
              {getEtudiants().map((etudiantSoutenance, index) => (
                <div key={index} className="flex items-center text-sm text-gray-700">
                  <span className="w-6 text-center">{index + 1}.</span>
                  <span className="flex-1">
                    {etudiantSoutenance.etudiant ? 
                      `${etudiantSoutenance.etudiant.nom || ''} ${etudiantSoutenance.etudiant.prenom || ''}`.trim() : 
                      'Étudiant non défini'} 
                  </span>
                  {etudiantSoutenance.role && (
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {etudiantSoutenance.role.replace('_', ' ')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Encadrement */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
          <User className="h-5 w-5" />
          <span>Encadrement</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium text-gray-700">Directeur:</span>
            <p className="text-gray-900">
              {soutenance.directeur_info?.nom || 
               (soutenance.directeur_interne && 
                `${soutenance.directeur_interne.prenom || ''} ${soutenance.directeur_interne.nom || ''}`.trim()) ||
               (soutenance.directeur_externe && soutenance.directeur_externe.utilisateur && 
                `${soutenance.directeur_externe.utilisateur.first_name || ''} ${soutenance.directeur_externe.utilisateur.last_name || ''}`.trim()) || 
               'Non défini'}
              {soutenance.directeur_info?.type && (
                <span className="text-xs text-gray-500 ml-1">
                  ({soutenance.directeur_info.type})
                </span>
              )}
            </p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700">Co-directeur:</span>
            <p className="text-gray-900">
              {soutenance.co_directeur_info?.nom || 
               (soutenance.co_directeur_interne && 
                `${soutenance.co_directeur_interne.prenom || ''} ${soutenance.co_directeur_interne.nom || ''}`.trim()) ||
               (soutenance.co_directeur_externe && soutenance.co_directeur_externe.utilisateur && 
                `${soutenance.co_directeur_externe.utilisateur.first_name || ''} ${soutenance.co_directeur_externe.utilisateur.last_name || ''}`.trim()) || 
               'Aucun'}
              {soutenance.co_directeur_info?.type && (
                <span className="text-xs text-gray-500 ml-1">
                  ({soutenance.co_directeur_info.type})
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Planification */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
          <Calendar className="h-5 w-5" />
          <span>Planification</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium text-gray-700">Date:</span>
            <p className="text-gray-900">
              {formatDate(soutenance.date_soutenance)}
            </p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700">Heure:</span>
            <p className="text-gray-900">
              {formatTime(soutenance.date_soutenance)}
            </p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700">Salle:</span>
            <p className="text-gray-900">{soutenance.salle?.libelle || soutenance.salle?.nom || 'Non définie'}</p>
            {soutenance.salle?.capacite && (
              <span className="text-xs text-gray-500">
                Capacité: {soutenance.salle.capacite} personnes
              </span>
            )}
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700">Durée:</span>
            <p className="text-gray-900">
              {soutenance.duree_heures || formatDuration(soutenance.duree_prevue) || 'Non définie'}
            </p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700">Jury:</span>
            <p className="text-gray-900">{soutenance.jury?.nom || 'Non défini'}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700">Statut:</span>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatutColor(soutenance.statut)}`}>
              {soutenance.statut_display || soutenance.statut || 'PLANIFIEE'}
            </span>
          </div>
        </div>
      </div>

      {/* Jury */}
      {soutenance.jury && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Composition du jury</span>
          </h4>
          <div className="space-y-3">
            {/* Président */}
            {soutenance.jury.president_info && (
              <div>
                <span className="text-sm font-medium text-gray-700">Président:</span>
                <p className="text-gray-900">
                  {soutenance.jury.president_info.nom}
                  <span className="text-xs text-gray-500 ml-1">
                    ({soutenance.jury.president_info.type})
                  </span>
                </p>
              </div>
            )}
            
            {/* Membres */}
            {soutenance.jury.membres_jury && soutenance.jury.membres_jury.length > 0 && (
              <div>
                <span className="text-sm font-medium text-gray-700">Membres:</span>
                <div className="mt-1 space-y-1">
                  {soutenance.jury.membres_jury.map((membre, index) => (
                    <p key={index} className="text-gray-900 text-sm">
                      • {membre.enseignant_info?.nom || 'Nom non disponible'} - {membre.role}
                      <span className="text-xs text-gray-500 ml-1">
                        ({membre.enseignant_info?.type})
                      </span>
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Résultats */}
      {(soutenance.note_finale || soutenance.mention) && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
            <Award className="h-5 w-5" />
            <span>Résultats</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {soutenance.note_finale && (
              <div>
                <span className="text-sm font-medium text-gray-700">Note finale:</span>
                <p className="text-gray-900 font-semibold">{soutenance.note_finale}/20</p>
              </div>
            )}
            {soutenance.mention && (
              <div>
                <span className="text-sm font-medium text-gray-700">Mention:</span>
                <p className="text-gray-900">{soutenance.mention_display || soutenance.mention}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Observations */}
      {soutenance.observations && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Observations</span>
          </h4>
          <p className="text-gray-700">{soutenance.observations}</p>
        </div>
      )}

      {/* Documents */}
      {soutenance.documents && soutenance.documents.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Documents ({soutenance.documents.length})</span>
          </h4>
          <div className="space-y-2">
            {soutenance.documents.map((document, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{document.titre}</p>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-xs text-gray-500">{document.type_document}</span>
                    {document.taille_fichier_mb && (
                      <span className="text-xs text-gray-500">{document.taille_fichier_mb} MB</span>
                    )}
                    <span className="text-xs text-gray-500">
                      {new Date(document.date_upload).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {document.fichier_url && (
                    <>
                      <a
                        href={document.fichier_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        title="Voir le document"
                      >
                        Voir
                      </a>
                      <a
                        href={document.fichier_url}
                        download
                        className="text-green-600 hover:text-green-800 text-xs font-medium"
                        title="Télécharger"
                      >
                        Télécharger
                      </a>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SoutenanceDetails; 